<template>
  <div>
    <!-- banner -->
    <div class="banner" :style="cover">
      <h1 class="banner-title">关于我</h1>
    </div>
    <!-- 简历内容（原「博主头像 + 关于我文案」区域改为展示简历） -->
    <v-card class="blog-container">
      <!-- 简历加载中 -->
      <div v-if="resumeLoading" class="resume-loading">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <!-- 简历 -->
      <div v-else-if="resume" class="resume-wrapper">
        <!-- 下载入口：可选择下载 PDF 或 Word -->
        <div class="resume-toolbar">
          <v-menu offset-y transition="slide-y-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="primary" depressed small :loading="pdfLoading" v-bind="attrs" v-on="on">
                <v-icon left small>mdi-download</v-icon>
                下载简历
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="downloadPdf">
                <v-list-item-title>下载 PDF</v-list-item-title>
              </v-list-item>
              <v-list-item @click="downloadWord">
                <v-list-item-title>下载 Word（原始文档）</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <!-- 简历正文：导出 PDF 时截取该区域 -->
        <div ref="resumeContent" class="resume-content">
          <!-- 基本信息 -->
          <div class="resume-header">
            <span class="resume-name">{{ resume.name }}</span>
            <span class="resume-job" v-if="resume.jobIntention">{{ resume.jobIntention }}</span>
          </div>
          <!-- 个人信息 -->
          <div class="resume-section">
            <div class="resume-section-title">个人信息</div>
            <div class="resume-info">
              <div class="resume-info-item" v-for="item in infoList" :key="item.label">
                <span class="resume-info-label">{{ item.label }}</span>
                <span class="resume-info-value">{{ item.value }}</span>
              </div>
            </div>
            <div class="resume-cert" v-if="certificateList.length">
              <span class="resume-info-label">证书</span>
              <div class="resume-cert-list">
                <div class="resume-cert-item" v-for="(cert, index) in certificateList" :key="index">{{ cert }}</div>
              </div>
            </div>
          </div>
          <!-- 个人技能 -->
          <div class="resume-section" v-if="skillList.length">
            <div class="resume-section-title">个人技能</div>
            <ul class="resume-list resume-skill-list">
              <li class="resume-list-item" v-for="(skill, index) in skillList" :key="index">{{ skill }}</li>
            </ul>
          </div>
          <!-- 项目经历 -->
          <div class="resume-section" v-if="projectList.length">
            <div class="resume-section-title">项目经历</div>
            <div class="resume-project" v-for="(project, index) in projectList" :key="project.id || index">
              <div class="resume-project-head">
                <span class="resume-project-name">{{ project.projectName }}</span>
                <span class="resume-project-type" v-if="project.projectType">{{ project.projectType }}</span>
                <span class="resume-project-time">{{ projectTime(project) }}</span>
              </div>
              <div class="resume-project-company" v-if="project.company">{{ project.company }}</div>
              <div class="resume-project-desc" v-if="project.description">{{ project.description }}</div>
              <div class="resume-project-tech" v-if="project.technologies">
                <span class="resume-project-tech-label">所用技术：</span>{{ project.technologies }}
              </div>
              <div class="resume-project-features" v-if="projectFeatures(project).length">
                <div class="resume-project-features-label">功能点（{{ projectFeatureCount(project) }} 个）</div>
                <ul class="resume-list resume-feature-list">
                  <li class="resume-list-item" v-for="(feature, fi) in projectFeatures(project)" :key="fi">{{ feature }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 后台尚未配置简历时，降级展示原有的「关于我」文案 -->
      <div v-else ref="about" class="about-content markdown-body" v-html="aboutContent" />
    </v-card>
  </div>
</template>

<script>
import Clipboard from "clipboard";
import { getResumeInfo, getResumeWordUrl } from "@/api/index";
export default {
  created() {
    this.getResume();
  },
  destroyed() {
    // 仅在渲染了「关于我」文案（markdown 降级展示）时才存在 clipboard 实例
    if (this.clipboard) {
      this.clipboard.destroy();
    }
  },
  metaInfo: {
    meta: [{
      name: 'keyWords',
      content: ""  //变量或字符串
    }, {
      name: 'description',
      content: ""
    }]
  },
  data: function () {
    return {
      aboutContent: "",
      img: process.env.VUE_APP_IMG_API,
      clipboard: null,
      imgList: [],
      // 简历相关
      resumeLoading: true,
      resume: null,
      pdfLoading: false
    };
  },
  methods: {
    // 简历数据在页面内单独请求，不影响原有 blogInfo（站点信息）逻辑
    getResume() {
      const that = this;
      getResumeInfo()
        .then(res => {
          that.resume = res.data || null;
          that.resumeLoading = false;
          if (!that.resume) {
            that.getAboutContent();
          }
        })
        .catch(() => {
          that.resumeLoading = false;
          if (!that.resume) {
            that.getAboutContent();
          }
        });
    },
    getAboutContent() {
      const that = this;
      var aboutMe = this.$store.state.blogInfo.webSite.aboutMe;
      this.markdownToHtml(aboutMe);
      this.$nextTick(() => {
        // 添加代码复制功能
        this.clipboard = new Clipboard(".copy-btn");
        this.clipboard.on("success", () => {
          this.$toast({ type: "success", message: "复制成功" });
        });
        // 添加图片预览功能
        const imgList = this.$refs.about.getElementsByTagName("img");
        for (var i = 0; i < imgList.length; i++) {
          this.imgList.push(imgList[i].src);
          imgList[i].addEventListener("click", function (e) {
            that.previewImg(e.target.currentSrc);
          });
        }
      });
    },
    markdownToHtml(data) {
      const MarkdownIt = require("markdown-it");
      const hljs = require("highlight.js");
      const md = new MarkdownIt({
        html: true,
        linkify: true,
        typographer: true,
        highlight: function (str, lang) {
          // 当前时间加随机数生成唯一的id标识
          var d = new Date().getTime();
          if (
            window.performance &&
            typeof window.performance.now === "function"
          ) {
            d += performance.now();
          }
          const codeIndex = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
            /[xy]/g,
            function (c) {
              var r = (d + Math.random() * 16) % 16 | 0;
              d = Math.floor(d / 16);
              return (c == "x" ? r : (r & 0x3) | 0x8).toString(16);
            }
          );
          // 复制功能主要使用的是 clipboard.js
          let html = `<button class="copy-btn iconfont iconfuzhi" type="button" data-clipboard-action="copy" data-clipboard-target="#copy${codeIndex}"></button>`;
          const linesLength = str.split(/\n/).length - 1;
          // 生成行号
          let linesNum = '<span aria-hidden="true" class="line-numbers-rows">';
          for (let index = 0; index < linesLength; index++) {
            linesNum = linesNum + "<span></span>";
          }
          linesNum += "</span>";
          if (lang && hljs.getLanguage(lang)) {
            // highlight.js 高亮代码
            const preCode = hljs.highlight(lang, str, true).value;
            html = html + preCode;
            if (linesLength) {
              html += '<b class="name">' + lang + "</b>";
            }
            // 将代码包裹在 textarea 中，由于防止textarea渲染出现问题，这里将 "<" 用 "<" 代替，不影响复制功能
            return `<pre class="hljs"><code>${html}</code>${linesNum}</pre><textarea style="position: absolute;top: -9999px;left: -9999px;z-index: -9999;" id="copy${codeIndex}">${str.replace(
              /<\/textarea>/g,
              "</textarea>"
            )}</textarea>`;
          }
        }
      });
      // 将markdown替换为html标签
      this.aboutContent = md.render(data);
    },
    previewImg(img) {
      this.$imagePreview({
        images: this.imgList,
        index: this.imgList.indexOf(img)
      });
    },
    // 项目起止时间
    projectTime(project) {
      const start = project.startTime || "";
      const end = project.endTime || "";
      if (start && end) {
        return start + " - " + end;
      }
      return start || end || "";
    },
    // 项目功能点列表
    projectFeatures(project) {
      return (project && project.features) || [];
    },
    // 项目功能数量（后端已给出 featureCount，缺失时按功能点个数兜底）
    projectFeatureCount(project) {
      if (project && project.featureCount) {
        return project.featureCount;
      }
      return this.projectFeatures(project).length;
    },
    // 下载 Word：后端 /web/resume/downloadWord 返回归档的原始 docx 附件
    downloadWord() {
      const link = document.createElement("a");
      link.href = getResumeWordUrl();
      link.target = "_blank";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.$toast({ type: "success", message: "Word 简历开始下载" });
    },
    // 下载 PDF：按需加载导出依赖，避免拖慢关于页首屏
    downloadPdf() {
      if (this.pdfLoading) {
        return;
      }
      const that = this;
      this.pdfLoading = true;
      Promise.all([import("html2canvas"), import("jspdf")])
        .then(([html2canvasModule, jspdfModule]) => {
          const html2canvas = html2canvasModule.default || html2canvasModule;
          const JsPDF =
            jspdfModule.jsPDF ||
            (jspdfModule.default && jspdfModule.default.jsPDF) ||
            jspdfModule.default;
          that.exportPdf(html2canvas, JsPDF);
        })
        .catch(() => {
          that.pdfLoading = false;
          that.$toast({ type: "error", message: "PDF 导出失败，请稍后重试" });
        });
    },
    // 把当前简历渲染为图片再导出 PDF（A4 分页，中文随图片输出，不会乱码）
    exportPdf(html2canvas, JsPDF) {
      const that = this;
      this.$nextTick(() => {
        const target = this.$refs.resumeContent;
        // 导出时固定浅色底、深色字，避免深色主题下导出的 PDF 不可读
        target.classList.add("pdf-export");
        const reset = () => {
          target.classList.remove("pdf-export");
          that.pdfLoading = false;
        };
        html2canvas(target, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          scrollX: 0,
          scrollY: -window.scrollY,
          windowWidth: document.documentElement.clientWidth
        })
          .then(canvas => {
            const pdf = new JsPDF("p", "mm", "a4");
            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 10;
            const contentWidth = pageWidth - margin * 2;
            const contentHeight = pageHeight - margin * 2;
            const imgWidth = contentWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const imgData = canvas.toDataURL("image/jpeg", 0.92);
            pdf.addImage(imgData, "JPEG", margin, margin, imgWidth, imgHeight);
            let heightLeft = imgHeight - contentHeight;
            let page = 1;
            while (heightLeft > 0) {
              pdf.addPage();
              pdf.addImage(
                imgData,
                "JPEG",
                margin,
                margin - page * contentHeight,
                imgWidth,
                imgHeight
              );
              heightLeft -= contentHeight;
              page++;
            }
            pdf.save(that.resumeFileName + ".pdf");
            reset();
            that.$toast({ type: "success", message: "PDF 简历导出成功" });
          })
          .catch(() => {
            reset();
            that.$toast({ type: "error", message: "PDF 导出失败，请稍后重试" });
          });
      });
    }
  },
  computed: {
    resumeFileName() {
      const resume = this.resume || {};
      const job = resume.jobIntention ? "-" + resume.jobIntention : "";
      return (resume.name || "简历") + job + "-简历";
    },
    infoList() {
      const resume = this.resume;
      if (!resume) {
        return [];
      }
      const list = [
        { label: "生日", value: resume.birthday },
        { label: "专业", value: resume.major },
        { label: "电话", value: resume.phone },
        { label: "邮箱", value: resume.email },
        { label: "教育背景", value: resume.education },
        { label: "博客", value: resume.blog }
      ];
      return list.filter(item => !!item.value);
    },
    certificateList() {
      const certificate = (this.resume && this.resume.certificate) || "";
      return certificate
        .split(/[\r\n；;]+/)
        .map(item => item.trim())
        .filter(item => !!item);
    },
    skillList() {
      return (this.resume && this.resume.skills) || [];
    },
    projectList() {
      return (this.resume && this.resume.projects) || [];
    },
    cover() {
      var cover = "";
      this.$store.state.blogInfo.pageList.forEach(item => {
        if (item.pageLabel === "about") {
          cover = item.pageCover;
        }
      });
      return "background: url(" + cover + ") center center / cover no-repeat";
    }
  }
};
</script>

<style scoped>
/* 后台尚未配置简历时展示的「关于我」文案（沿用原有样式） */
.about-content {
  word-break: break-word;
  line-height: 1.8;
  font-size: 14px;
}

.resume-loading {
  padding: 60px 0;
  text-align: center;
}

.resume-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 18px;
}

.resume-content {
  word-break: break-word;
  line-height: 1.8;
  font-size: 14px;
}

/* 导出 PDF 时固定为浅色底、深色字 */
.resume-content.pdf-export {
  background: #ffffff !important;
  color: #4c4948 !important;
  padding: 16px;
}

.resume-header {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  padding-bottom: 12px;
  margin-bottom: 22px;
  border-bottom: 1px solid rgba(128, 128, 128, 0.25);
}

.resume-name {
  font-size: 26px;
  font-weight: 600;
  letter-spacing: 2px;
}

.resume-job {
  margin-left: 12px;
  font-size: 15px;
  color: #49b1f5;
}

.resume-section {
  margin-bottom: 26px;
}

.resume-section-title {
  position: relative;
  padding-left: 12px;
  margin-bottom: 14px;
  font-size: 17px;
  font-weight: 600;
}

.resume-section-title::before {
  content: "";
  position: absolute;
  left: 0;
  top: 3px;
  bottom: 3px;
  width: 4px;
  border-radius: 2px;
  background: #49b1f5;
}

.resume-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  grid-gap: 8px 20px;
}

.resume-info-item {
  display: flex;
  align-items: baseline;
}

.resume-info-label {
  flex: 0 0 auto;
  min-width: 68px;
  margin-right: 8px;
  opacity: 0.6;
}

.resume-info-value {
  flex: 1 1 auto;
}

.resume-cert {
  display: flex;
  align-items: baseline;
  margin-top: 10px;
}

.resume-cert-list {
  flex: 1 1 auto;
}

.resume-cert-item {
  margin-bottom: 2px;
}

.resume-list {
  margin: 0;
  padding-left: 20px;
}

.resume-list-item {
  margin-bottom: 4px;
}

.resume-project {
  padding: 16px 18px;
  margin-bottom: 16px;
  border-radius: 8px;
  border-left: 3px solid #49b1f5;
  background: rgba(128, 128, 128, 0.07);
}

.resume-project-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.resume-project-name {
  margin-right: 8px;
  font-size: 16px;
  font-weight: 600;
}

.resume-project-type {
  margin-right: 8px;
  padding: 1px 8px;
  font-size: 12px;
  line-height: 18px;
  color: #49b1f5;
  border: 1px solid #49b1f5;
  border-radius: 10px;
}

.resume-project-time {
  font-size: 13px;
  opacity: 0.6;
}

.resume-project-company {
  margin-bottom: 6px;
  font-size: 13px;
  opacity: 0.75;
}

.resume-project-desc {
  margin-bottom: 8px;
}

.resume-project-tech {
  margin-bottom: 8px;
}

.resume-project-tech-label {
  opacity: 0.6;
}

.resume-project-features-label {
  margin-bottom: 4px;
  font-size: 13px;
  opacity: 0.6;
}

@media screen and (max-width: 600px) {
  .resume-name {
    font-size: 22px;
  }

  .resume-project {
    padding: 14px 12px;
  }

  .resume-info {
    grid-template-columns: repeat(auto-fit, minmax(100%, 1fr));
  }
}
</style>

<style lang="scss">
pre.hljs {
  padding: 12px 2px 12px 40px !important;
  border-radius: 5px !important;
  position: relative;
  font-size: 14px !important;
  line-height: 22px !important;
  overflow: hidden !important;

  &:hover .copy-btn {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  code {
    display: block !important;
    margin: 0 10px !important;
    overflow-x: auto !important;

    &::-webkit-scrollbar {
      z-index: 11;
      width: 6px;
    }

    &::-webkit-scrollbar:horizontal {
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 5px;
      width: 6px;
      background: #666;
    }

    &::-webkit-scrollbar-corner,
    &::-webkit-scrollbar-track {
      background: #1e1e1e;
    }

    &::-webkit-scrollbar-track-piece {
      background: #1e1e1e;
      width: 6px;
    }
  }

  .line-numbers-rows {
    position: absolute;
    pointer-events: none;
    top: 12px;
    bottom: 12px;
    left: 0;
    font-size: 100%;
    width: 40px;
    text-align: center;
    letter-spacing: -1px;
    border-right: 1px solid rgba(0, 0, 0, 0.66);
    user-select: none;
    counter-reset: linenumber;

    span {
      pointer-events: none;
      display: block;
      counter-increment: linenumber;

      &:before {
        content: counter(linenumber);
        color: #999;
        display: block;
        text-align: center;
      }
    }
  }

  b.name {
    position: absolute;
    top: 7px;
    right: 45px;
    z-index: 1;
    color: #999;
    pointer-events: none;
  }

  .copy-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 1;
    color: #ccc;
    background-color: #525252;
    border-radius: 6px;
    display: none;
    font-size: 14px;
    width: 32px;
    height: 24px;
    outline: none;
  }
}
</style>
